import { Show, createMemo } from "solid-js";
import { SourceTag } from "../../components/SourceTag.tsx";
import { Table } from "../../components/Table.tsx";
import { useI18n } from "../../i18n/provider.tsx";
import { formatScore } from "../../lib/format.ts";
import { answerComparison, type ComparisonRow } from "./overview.ts";
import type { Product, TreeQuestion } from "../../api/domain.ts";
import styles from "./QuestionAnswers.module.scss";

export type QuestionAnswersProps = {
  question: TreeQuestion;
  /**
   * The products to compare, chosen by the caller the way the matrix chooses
   * its columns. Every one of them holds a row, answered or not.
   */
  products: Product[];
};

/**
 * One question, every product's answer to it, one row each. This is the answer
 * matrix narrowed to a single question and turned on its side: on the catalog
 * screen the question is already the thing in hand, and the reader's next
 * question is how the products compare on it.
 *
 * The answer shown is the one that counts, the same one a report would read,
 * which is why it carries who answered and where the answer came from. An
 * answer that lost the precedence contest is not here; the product's own page
 * holds the full history.
 */
export const QuestionAnswers = (props: QuestionAnswersProps) => {
  const i18n = useI18n();
  const comparison = createMemo(() => answerComparison(props.question, props.products));

  const Value = (row: { row: ComparisonRow }) => (
    <Show
      when={row.row.answer}
      fallback={<span class={styles.absent}>{i18n.t().overview.noAnswerYet}</span>}
    >
      {(answer) => (
        <Show
          when={typeof answer().value === "number"}
          fallback={<span class={styles.text}>{answer().text ?? "—"}</span>}
        >
          <span class={styles.value}>
            {formatScore(answer().value as number, i18n.locale())}
          </span>
        </Show>
      )}
    </Show>
  );

  return (
    <div class={styles.panel}>
      <p class={styles.question}>{props.question.title}</p>
      <Show when={props.question.body !== undefined && props.question.body !== null}>
        <p class={styles.body}>{props.question.body}</p>
      </Show>
      <p class={styles.badges}>
        <span
          class={`${styles.badge} ${styles[props.question.coverage] ?? ""}`}
          title={i18n.t().coverage.hint[props.question.coverage]}
        >
          {i18n.t().coverage.label[props.question.coverage]}
        </span>
        <Show when={props.question.informational}>
          <span class={styles.badge}>{i18n.t().catalog.informational}</span>
        </Show>
      </p>

      <Table
        rows={comparison().rows}
        rowKey={(row) => row.product}
        emptyLabel={i18n.t().overview.noProducts}
        columns={[
          {
            key: "product",
            header: i18n.t().common.product,
            cell: (row) => <span class={styles.product}>{row.name}</span>,
          },
          {
            key: "answer",
            header: i18n.t().common.answer,
            cell: (row) => <Value row={row} />,
          },
          {
            key: "source",
            header: i18n.t().overview.origin,
            cell: (row) => (
              <Show when={row.answer}>
                {(answer) => (
                  <SourceTag source={answer().source} confidence={answer().confidence} />
                )}
              </Show>
            ),
          },
          {
            key: "author",
            header: i18n.t().common.author,
            cell: (row) => <span class={styles.author}>{row.answer?.author ?? ""}</span>,
          },
        ]}
      />

      {/* Not a score: the plain mean of what these products answered here. */}
      <p class={styles.summary}>
        <Show when={comparison().mean !== undefined}>
          <span class={styles.mean}>
            {i18n.t().overview.average} {formatScore(comparison().mean as number, i18n.locale())}
          </span>
        </Show>
        <span class={styles.thinness}>
          {comparison().answered} {i18n.t().overview.answeredOf} {comparison().total}
        </span>
      </p>
      <p class={styles.caveat}>{i18n.t().overview.caveat}</p>
    </div>
  );
};
